import { useState } from 'react';
import { FaKey, FaPalette } from 'react-icons/fa';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../lib/useAuth';
import { useTheme, CLASSIC_SETTING_KEY } from '../../lib/theme';
import { saveContent } from '../../lib/adminApi';

const DesignCard = () => {
  const { classicEnabled, setClassicEnabled } = useTheme();
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const toggle = async () => {
    const next = !classicEnabled;
    setSaving(true);
    setMsg(null);
    try {
      await saveContent([{
        key: CLASSIC_SETTING_KEY,
        label: 'Ustawienie: stary (klasyczny) design dostępny',
        value_pl: String(next),
        value_en: String(next),
      }]);
      setClassicEnabled(next);
      setMsg({ type: 'ok', text: next
        ? 'Stary design odblokowany — na stronie pojawił się przełącznik wyglądu.'
        : 'Stary design ukryty — strona pokazuje wyłącznie nowy (ciemny) design.' });
    } catch (err) {
      setMsg({ type: 'err', text: 'Błąd zapisu: ' + err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <FaPalette className="text-brand-accent" />
        <h3 className="text-lg font-semibold text-gray-800">Wygląd strony</h3>
      </div>

      <p className="text-sm text-gray-600 mb-5">
        Główny design strony to <span className="font-medium">nowy, ciemny (Imperial)</span>.
        Stary, klasyczny design jest ukryty — możesz go odblokować. Wtedy w nagłówku strony
        pojawi się przełącznik, którym odwiedzający mogą zmienić wygląd.
      </p>

      <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <div>
          <p className="text-sm font-medium text-gray-800">Stary (klasyczny) design</p>
          <p className="text-xs text-gray-500">{classicEnabled ? 'Odblokowany — dostępny przez przełącznik' : 'Ukryty'}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={classicEnabled}
          aria-label="Odblokuj stary design"
          onClick={toggle}
          disabled={saving}
          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent disabled:opacity-50 ${
            classicEnabled ? 'bg-brand-accent' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-300 ${
              classicEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {msg && (
        <div className={`mt-4 text-sm rounded-md p-3 ${msg.type === 'ok' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
          {msg.text}
        </div>
      )}
    </div>
  );
};

const SettingsPage = () => {
  const { session } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (password.length < 8) { setMsg({ type: 'err', text: 'Hasło musi mieć co najmniej 8 znaków.' }); return; }
    if (password !== confirm) { setMsg({ type: 'err', text: 'Hasła nie są identyczne.' }); return; }
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setMsg({ type: 'ok', text: 'Hasło zostało zmienione.' });
      setPassword(''); setConfirm('');
    } catch (err) { setMsg({ type: 'err', text: 'Błąd: ' + err.message }); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg space-y-6">
      <DesignCard />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <FaKey className="text-brand-accent" />
          <h3 className="text-lg font-semibold text-gray-800">Konto</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">Zalogowany jako <span className="font-medium">{session?.user?.email}</span></p>

        <form onSubmit={changePassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nowe hasło</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" autoComplete="new-password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Powtórz nowe hasło</label>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" autoComplete="new-password" />
          </div>
          {msg && (
            <div className={`text-sm rounded-md p-3 ${msg.type === 'ok' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>{msg.text}</div>
          )}
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-md bg-brand-accent text-white hover:opacity-90 disabled:opacity-50">
            {saving ? 'Zapisywanie...' : 'Zmień hasło'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SettingsPage;
